import * as auth0 from '@auth0/auth0-vue';
import { unref, watchEffect, computed } from 'vue';

function watchEffectOnceAsync<T>(watcher: () => T) {
  return new Promise<void>((resolve) => {
    watchEffectOnce(watcher, resolve);
  });
}

/**
 * @ignore
 * Run watchEffect untill the watcher returns true, then stop the watch.
 * Once it returns true, it will call the provided function.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function watchEffectOnce<T>(watcher: () => T, fn: Function) {
  const stopWatch = watchEffect(() => {
    if (watcher()) {
      fn();
      stopWatch();
    }
  });
}

// Define the Auth0 User interface based on your .NET backend data
export interface Auth0User {
  sub: string;
  given_name: string;
  family_name: string;
  nickname: string;
  name: string;
  picture: string;
  updated_at: string;
  email: string;
  email_verified: boolean;
}

export const useAuth = () => {
  const auth = auth0.useAuth0();

  const guardHandler = async () => {
    const fn = async () => {
      if (unref(auth.isAuthenticated)) {
        return true;
      }
      return false;
    };
    if (!unref(auth.isLoading)) {
      return fn();
    }
    await watchEffectOnceAsync(() => !unref(auth.isLoading));
    return fn();
  };

  // NEW: Access Auth0 user data directly
  const auth0User = computed((): Auth0User | null => {
    return unref(auth.user) as Auth0User | null;
  });

  // NEW: Helper getters for common Auth0 user data
  const userFullName = computed(() => auth0User.value?.name || '');
  const userGivenName = computed(() => auth0User.value?.given_name || '');
  const userFamilyName = computed(() => auth0User.value?.family_name || '');
  const userNickname = computed(() => auth0User.value?.nickname || '');
  const userPicture = computed(() => auth0User.value?.picture || '');
  const userEmail = computed(() => auth0User.value?.email || '');
  const userEmailVerified = computed(() => auth0User.value?.email_verified || false);
  const userSub = computed(() => auth0User.value?.sub || '');

  return {
    auth,
    guardHandler,
    auth0User,
    userFullName,
    userGivenName,
    userFamilyName,
    userNickname,
    userPicture,
    userEmail,
    userEmailVerified,
    userSub
  };
};